import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  CHALLENGE_COURSE_SPEND_OPTIONS,
  CHALLENGE_INVESTING_EXPERIENCE_OPTIONS,
  CHALLENGE_QUESTION_TEXT,
  type ChallengeCourseSpend,
  type ChallengeInvestingExperience,
} from './challengeQualifyingLead';
import './ChallengeFormQuestions.css';

export type ChallengeFormQuestionAnswers = {
  investingExperience?: ChallengeInvestingExperience;
  courseSpend?: ChallengeCourseSpend;
};
export type ChallengeFormQuestionKey = keyof ChallengeFormQuestionAnswers;

const QUESTIONS = {
  investingExperience: {
    step: 2,
    text: CHALLENGE_QUESTION_TEXT.investingExperience,
    options: CHALLENGE_INVESTING_EXPERIENCE_OPTIONS,
  },
  courseSpend: {
    step: 3,
    text: CHALLENGE_QUESTION_TEXT.courseSpend,
    options: CHALLENGE_COURSE_SPEND_OPTIONS,
  },
} as const;

/**
 * How long a question's answers ignore pointer taps after it appears. The
 * answers land where the visitor just pressed Save (or the previous answer),
 * so the second half of a quick double tap would otherwise answer a question
 * they have not read, and send Google Ads a wrong value (Rob, 2026-10-02).
 * Keyboard presses are never held back.
 */
export const CHALLENGE_QUESTION_TAP_GUARD_MS = 350;

const HEADER_GAP_PX = 8;

function fixedHeaderBottom(): number {
  const bars = document.querySelectorAll<HTMLElement>('[role="banner"] nav, header nav');
  let bottom = 0;
  bars.forEach((bar) => {
    const rect = bar.getBoundingClientRect();
    if (rect.height > 0 && rect.top <= 1) bottom = Math.max(bottom, rect.bottom);
  });
  return bottom;
}

/**
 * Steps 2 and 3 of the free challenge signup, inside the same form box as
 * step 1 (Rob, 2026-10-02). The seat is already saved. One question at a time;
 * a tap saves the answer and moves on, so there is no Next or Submit button.
 * The page reports every tap and finishes once both are in.
 *
 * Focus moves to the question for screen readers with preventScroll. The only
 * scroll is one instant nudge when the box top is hidden above the screen or
 * under the fixed site header, so the step line and question are readable
 * (Rob, 2026-10-03). Once the box top is in view, later questions never scroll.
 */
export default function ChallengeFormQuestions({
  current,
  onShow,
  onAnswer,
  notice = null,
}: {
  /** The question on show. The page owns it so the form box can name its step. */
  current: ChallengeFormQuestionKey;
  onShow: (question: ChallengeFormQuestionKey) => void;
  onAnswer: (
    answers: ChallengeFormQuestionAnswers,
    changed: ChallengeFormQuestionKey,
  ) => void | Promise<void>;
  notice?: React.ReactNode;
}): React.ReactElement {
  const [answers, setAnswers] = useState<ChallengeFormQuestionAnswers>({});
  const finished = useRef(false);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pendingAnswer = useRef<{
    answers: ChallengeFormQuestionAnswers;
    changed: ChallengeFormQuestionKey;
  } | null>(null);
  const questionRef = useRef<HTMLParagraphElement>(null);
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  const pointerStartedWhileGuarded = useRef(false);

  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const box = rootRef.current?.closest('form');
    if (!box) return;
    const wanted = fixedHeaderBottom() + HEADER_GAP_PX;
    const { top, height } = box.getBoundingClientRect();
    if (height > 0 && top < wanted) {
      // SAFETY: Firefox and Chrome implement instant scrolling; the older TypeScript DOM union omits that browser value.
      window.scrollBy({ top: top - wanted, left: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [current]);

  useEffect(() => {
    questionRef.current?.focus({ preventScroll: true });
    readyRef.current = false;
    setReady(false);
    const timer = window.setTimeout(() => {
      readyRef.current = true;
      setReady(true);
    }, CHALLENGE_QUESTION_TAP_GUARD_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  const save = async (next: ChallengeFormQuestionAnswers, changed: ChallengeFormQuestionKey) => {
    if (savingRef.current || finished.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    pendingAnswer.current = { answers: next, changed };
    try {
      await onAnswer(next, changed);
      if (changed === 'investingExperience') onShow('courseSpend');
      else finished.current = true;
    } catch {
      setSaveError('Your seat is saved, but these answers could not be saved. Please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const choose = (value: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (finished.current || savingRef.current) return;
    // detail is 0 for a keyboard press, 1+ for a pointer tap.
    if (event.detail > 0) {
      const guardedPress = pointerStartedWhileGuarded.current;
      pointerStartedWhileGuarded.current = false;
      // A press that began before the answers were ready stays ignored even
      // when its release arrives after the guard expires.
      if (guardedPress || !readyRef.current) return;
    }
    const next: ChallengeFormQuestionAnswers = { ...answers, [current]: value };
    setAnswers(next);
    void save(next, current);
  };

  const question = QUESTIONS[current];
  const selected = answers[current] ?? null;

  return (
    <div
      ref={rootRef}
      className="chfq"
      data-testid="challenge-qualifying-step"
      data-question={current}
      data-ready={ready ? 'true' : 'false'}
      role="group"
      aria-labelledby="chfq-question"
    >
      {notice ? (
        <p className="chfq-notice" role="status">
          {notice}
        </p>
      ) : null}
      <p className="chfq-progress">Step {question.step} of 3</p>
      <p className="chfq-question" id="chfq-question" ref={questionRef} tabIndex={-1}>
        {question.text}
      </p>
      {saving ? <p role="status">Saving your answer…</p> : null}
      {saveError ? (
        <div role="alert">
          <p>{saveError}</p>
          <button
            type="button"
            className="chfq-option"
            disabled={saving}
            onClick={() => {
              const pending = pendingAnswer.current;
              if (pending) void save(pending.answers, pending.changed);
            }}
          >
            Try again
          </button>
        </div>
      ) : null}
      <div className="chfq-options" data-testid={`challenge-qualifying-${current}`}>
        {question.options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={saving}
            className="chfq-option"
            aria-pressed={selected === option.value}
            data-value={option.value}
            onPointerDown={() => {
              pointerStartedWhileGuarded.current = !readyRef.current;
            }}
            onPointerCancel={() => {
              pointerStartedWhileGuarded.current = false;
            }}
            onClick={(event) => choose(option.value, event)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {current === 'courseSpend' ? (
        <button
          type="button"
          className="chfq-back"
          disabled={saving}
          onClick={() => {
            pendingAnswer.current = null;
            setSaveError(null);
            setAnswers({ investingExperience: answers.investingExperience });
            onShow('investingExperience');
          }}
        >
          Back
        </button>
      ) : null}
    </div>
  );
}
